"use client";

import type { Media } from "@mw/shared/payload-types";
import type { FC } from "react";

import clsx from "clsx";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import NextImage from "next/image";

import { Noise } from "@ui/components/core/Noise/Noise";
import { getImageProps } from "@utilities/payload";

import { BookNavigationArrows } from "./BookNavigationArrows";

// Cutout dimensions for the book cover window
const CUTOUT_WIDTH_REM = 19.25; // 308px
const CUTOUT_HEIGHT_REM = 25.375; // 406px

const centeredAbsolute = "tw-absolute tw-left-1/2 tw-top-[calc(50%-1rem)] -tw-translate-x-1/2 -tw-translate-y-1/2";

const cutoutStyle = {
  width: `${CUTOUT_WIDTH_REM}rem`,
  height: `${CUTOUT_HEIGHT_REM}rem`,
} as const;

export interface BookCoverCardProps {
  coverImage: string | Media;
  backgroundImage: string | Media;
  title: string;
  subtitle?: string | null;
  author?: string | null;
  onPrev?: () => void;
  onNext?: () => void;
}

export const BookCoverCard: FC<BookCoverCardProps> = ({
  coverImage,
  backgroundImage,
  title,
  subtitle,
  author,
  onPrev,
  onNext,
}) => {
  const imageProps = getImageProps(coverImage, { fill: true, placeholder: true });
  const backgroundProps = getImageProps(backgroundImage, { fill: true, placeholder: true });
  const imageKey = typeof coverImage === "string" ? coverImage : coverImage.id;

  if (!imageProps || !backgroundProps) {
    return null;
  }

  return (
    <div className='tw-relative tw-h-full tw-overflow-hidden tw-rounded-2xl tw-bg-black'>
      <AnimatePresence mode='popLayout'>
        <m.div
          key={imageKey}
          className='tw-absolute tw-inset-0'
          initial={{ opacity: 0.5 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Background */}
          <NextImage {...backgroundProps} alt='' className='tw-h-full tw-w-full tw-object-cover' />

          {/* Sharp cutout window */}
          <div className={clsx(centeredAbsolute, "tw-overflow-hidden tw-rounded-md")} style={cutoutStyle}>
            <NextImage {...imageProps} alt='' className='tw-h-full tw-w-full tw-object-cover' />
          </div>

          <Noise className='tw-rounded-2xl tw-opacity-70' />

          {/* Cutout border overlay */}
          <div
            className={clsx(centeredAbsolute, "tw-rounded-md tw-border-0.5 tw-border-white/30 tw-bg-white/10")}
            style={cutoutStyle}
          />

          {/* Book info overlay */}
          <div
            className={clsx(centeredAbsolute, "tw-flex tw-flex-col tw-justify-between tw-p-6 tw-text-white")}
            style={cutoutStyle}
          >
            <div className='tw-flex tw-w-2/3 tw-flex-col tw-gap-5 tw-text-left'>
              <span className='tw-type-4xl'>{title}</span>
              {subtitle && <span className='tw-type-2xs tw-opacity-80'>{subtitle}</span>}
            </div>
            {author && (
              <span className='tw-type-sm tw-self-end tw-text-balance tw-text-right tw-opacity-80'>{author}</span>
            )}
          </div>
        </m.div>
      </AnimatePresence>

      {/* Navigation arrows */}
      <BookNavigationArrows
        onPrev={onPrev}
        onNext={onNext}
        className='tw-absolute tw-bottom-8 tw-left-1/2 tw-z-10 -tw-translate-x-1/2'
      />
    </div>
  );
};
